/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { ROUTES } from 'constants';

import React, { useEffect } from 'react';
import { Loading } from 'carbon-addons-iot-react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';

import WelcomePage from '../WelcomePage/WelcomePage';

import { logout } from 'FetchAPIs';

const messages = defineMessages({
  errorMessage: 'Session timed out.',
});

const propTypes = {
  title: PropTypes.string.isRequired,
  /** We need the ssoLogoutURL so we can force SSO logout if our session is expired */
  ssoLogoutURL: PropTypes.string,
  tenantId: PropTypes.string,
  isICP: PropTypes.bool,
  docURL: PropTypes.string.isRequired,
};

const defaultProps = {
  tenantId: null,
  isICP: false,
  ssoLogoutURL: null,
};

const SessionTimeout = ({
  title,
  ssoLogoutURL,
  isICP,
  tenantId,
  docURL,
  intl: { formatMessage },
}) => {
  useEffect(() => sessionStorage.clear(), []);

  useEffect(() => {
    if (isICP) {
      // we're not authenticated on icp we need to send to server-side login page, can't use React Router redirect
      window.location.replace(ROUTES.LOGIN);
    }
  }, [isICP]);

  // Need to force logout just in-case they're not logged out
  useEffect(
    () => {
      const asyncLogout = async () => {
        if (ssoLogoutURL) {
          // this has 2 urls, comma separated
          const [authLogoutURL, uiLogoutURL] = ssoLogoutURL.split(',');
          authLogoutURL && fetch(authLogoutURL, { mode: 'no-cors', credentials: 'include' });
          uiLogoutURL && fetch(uiLogoutURL, { mode: 'no-cors', credentials: 'include' });
          logout();
        }
      };
      asyncLogout();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return isICP ? (
    <Loading />
  ) : (
    <section>
      <WelcomePage
        title={title}
        tenantId={tenantId}
        docURL={docURL}
        errorMessage={formatMessage(messages.errorMessage)}
      />
    </section>
  );
};

SessionTimeout.propTypes = propTypes;
SessionTimeout.defaultProps = defaultProps;

export default injectIntl(SessionTimeout);
